import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Avatar,
  Typography,
  IconButton,
  Chip,
  Stack,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  Favorite as FavoriteIcon,
} from '@mui/icons-material';
import { TreeNode } from '../../types';

/**
 * TreePersonNode Component
 *
 * Individual person node in the family tree
 *
 * Displays:
 * - Person photo (thumbnail) or avatar fallback
 * - Name (clickable to view profile)
 * - Birth-death years
 * - Spouse(s) if applicable
 * - Expand/collapse button if has children
 * - Generation level indicator
 *
 * @component
 */
interface TreePersonNodeProps {
  node: TreeNode;
  onPersonClick: (personId: string) => void;
  onToggleExpand?: () => void;
}

export const TreePersonNode: React.FC<TreePersonNodeProps> = ({
  node,
  onPersonClick,
  onToggleExpand,
}) => {
  const { person, spouses, children, level, isExpanded } = node;

  // ===== Helper Functions =====

  /**
   * Format birth-death years
   */
  const formatYears = (birthDate?: string, deathDate?: string): string => {
    const birth = birthDate ? new Date(birthDate).getFullYear() : '?';
    const death = deathDate ? new Date(deathDate).getFullYear() : 'present';
    return `${birth} - ${death}`;
  };

  /**
   * Get person initials for avatar
   */
  const getInitials = (firstName: string, lastName: string): string => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  /**
   * Get avatar color based on gender
   */
  const getAvatarColor = (gender?: string): string => {
    switch (gender) {
      case 'male':
        return '#1976d2'; // Blue
      case 'female':
        return '#d81b60'; // Pink
      default:
        return '#757575'; // Gray
    }
  };

  // ===== Event Handlers =====

  const handlePersonClick = () => {
    onPersonClick(person.id);
  };

  const handleToggleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering person click
    if (onToggleExpand) {
      onToggleExpand();
    }
  };

  // ===== Render =====

  const hasChildren = children && children.length > 0;
  const canExpand = hasChildren && onToggleExpand;

  return (
    <Card
      sx={{
        display: 'inline-block',
        minWidth: 280,
        maxWidth: 320,
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-2px)',
        },
        borderLeft: `4px solid ${getAvatarColor(person.gender)}`,
      }}
      onClick={handlePersonClick}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          {/* Avatar */}
          <Avatar
            alt={`${person.firstName} ${person.lastName}`}
            sx={{
              width: 48,
              height: 48,
              bgcolor: getAvatarColor(person.gender),
              fontSize: '1.2rem',
            }}
          >
            {getInitials(person.firstName, person.lastName)}
          </Avatar>

          {/* Person Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Name */}
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                lineHeight: 1.2,
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {person.firstName} {person.lastName}
            </Typography>

            {/* Years */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {formatYears(person.birthDate, person.deathDate)}
            </Typography>

            {/* Generation Badge */}
            <Chip
              label={`Gen ${level + 1}`}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 600,
              }}
              color="primary"
              variant="outlined"
            />
          </Box>

          {/* Expand/Collapse Button */}
          {canExpand && (
            <IconButton
              size="small"
              onClick={handleToggleClick}
              sx={{
                ml: 'auto',
                alignSelf: 'center',
              }}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Box>

        {/* Spouses */}
        {spouses && spouses.length > 0 && (
          <Box sx={{ mt: 1.5, pl: 8 }}>
            <Stack spacing={0.5}>
              {spouses.map((spouseInfo, index) => (
                <Box
                  key={spouseInfo.person.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <FavoriteIcon sx={{ fontSize: 14, color: 'error.main' }} />
                  <Typography variant="caption" color="text.secondary">
                    {spouseInfo.person.firstName} {spouseInfo.person.lastName}
                  </Typography>
                  {spouseInfo.relationship.marriageDate && (
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                      (m. {new Date(spouseInfo.relationship.marriageDate).getFullYear()})
                    </Typography>
                  )}
                  {!spouseInfo.isCurrent && spouseInfo.relationship.divorceDate && (
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                      - divorced
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {/* Children Count (when collapsed) */}
        {hasChildren && !isExpanded && (
          <Box sx={{ mt: 1.5, pl: 8 }}>
            <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
              {children.length} {children.length === 1 ? 'child' : 'children'}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
